


































































































































































































































import ALPage from '@/components/core/Page/ALPage.vue';
import CountryFlag from 'vue-country-flag';
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';
import DetailOptionsButton from '@/components/common/DetailOptionsButton.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import EmployeeFormSubmissionDialog from '@/components/employee-form/EmployeeFormSubmissionDialog.vue';
import EmployeeFormSubmissionSearchFilter from '@/components/employee-form/EmployeeFormSubmissionSearchFilter.vue';
import EmployeeFormSubmissionActivityDialog from '@/components/employee-form/EmployeeFormSubmissionActivityDialog.vue';
import EmployeeFormSubmissionStatusDisplay from '@/components/employee-form/EmployeeFormSubmissionStatusDisplay.vue';

import { mapActions, mapGetters } from 'vuex';
import {
  EmployeeFormSubmission,
  EmployeeFormSubmissionStateEnum,
  FormTemplateStateEnum,
  IEmployeeFormSubmission
} from '@/models';
import { ErrorManager } from '@/models/error';

import moment from 'moment';
import { merge, omit } from 'lodash';

import {
  IEmployeeFormSubmissionFetchParams,
  IFetchFormTemplatesParams,
  IUpdateEmployeeFormSubmissionStatePayload
} from '@/services';

import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import auth from '@/mixins/auth';
import SnackBarMixin from '@/mixins/snackbar';
import { AxiosError } from 'axios';
import { createPdf } from '@/utils/helpers';

const baseMixins = mixins(Functions, auth, SnackBarMixin);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $refs: {
    employeeFormSubmissionDialog: InstanceType<
      typeof EmployeeFormSubmissionDialog
    >;

    employeeFormSubmissionActivityDialog: InstanceType<
      typeof EmployeeFormSubmissionActivityDialog
    >;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: {
      ALPage,
      CountryFlag,
      ConfirmationDialog,
      DetailOptionsButton,
      ErrorAlert,
      EmployeeFormSubmissionDialog,
      EmployeeFormSubmissionSearchFilter,
      EmployeeFormSubmissionActivityDialog,
      EmployeeFormSubmissionStatusDisplay
    },

    data() {
      return {
        loading: false,
        isExportLoading: false,
        isSearchFilterActive: false,

        errorMessage: '',
        detailErrorMessage: '',

        params: {
          search: '',
          page: 1
        } as IEmployeeFormSubmissionFetchParams,

        sortBy: 'id',
        sortDesc: true,

        headers: [
          {
            text: '#',
            sortable: false,
            value: 'index'
          },
          {
            text: 'Manager',
            sortable: false,
            value: 'owner'
          },
          {
            text: 'Title',
            sortable: false,
            value: 'form.title'
          },
          {
            text: 'Employee',
            sortable: false,
            value: 'employee'
          },
          {
            text: 'Last Modified',
            sortable: false,
            value: 'updated_at'
          },
          {
            text: 'Status',
            sortable: false,
            align: 'center',
            value: 'state'
          },
          {
            text: '',
            sortable: false,
            align: 'center',
            value: 'actions'
          }
        ],

        EmployeeFormSubmissionStateEnum,

        selectedEmployeeFormSubmission: {} as IEmployeeFormSubmission,
        cancelConfirmationDialog: false,
        commentsDialog: false,

        overtimeComment: '',
        actionLabel: '',

        commentErrorMessage: '',
        commentErrorMessageDetail: '',
        commentLoading: false,

        state: EmployeeFormSubmissionStateEnum.Draft
      };
    },

    computed: {
      ...mapGetters({
        getFormTemplateById: 'formTemplate/getFormTemplateById',
        employeeFormSubmissions: 'employeeForm/getEmployeeFormSubmissions',
        publishedFormTemplates: 'formTemplate/getPublishedFormTemplates',
        numSubmissions: 'employeeForm/getEmployeeFormSubmissionCount'
      }),

      extraFiltersApplied(): boolean {
        return Object.keys(omit(this.$route.query, ['page', 'search'])).some(
          v => !!v
        );
      }
    },

    watch: {
      'params.search'(search: string) {
        this.debounceFetchEmployeeFormSubmissions({
          ...this.params,
          search: search,
          page: 1
        }).then(() => {
          this.params.page = 1;
        });
      }
    },

    async mounted() {
      // update `this.params` from the route query object.
      // omitting `search` and `page` as they are already set in the data vue option
      merge(this.params, omit(this.$route.query, ['page', 'search']));

      this.loading = true;
      try {
        await this.searchEmployeeFormSubmissions(this.params);

        const templateParams = {
          state: FormTemplateStateEnum.Published
        } as IFetchFormTemplatesParams;
        await this.fetchFormTemplates(templateParams);
      } catch (err) {
        const error = err as AxiosError;
        if (error.response) {
          if (error.response.data.detail) {
            this.errorMessage = error.response.data.detail;
          }
        }
        this.errorMessage = ErrorManager.extractApiError(error);
      } finally {
        this.loading = false;
      }
    },

    methods: {
      moment,

      ...mapActions({
        fetchEmployeeFormSubmissions:
          'employeeForm/fetchEmployeeFormSubmissions',
        fetchFormTemplates: 'formTemplate/fetchFormTemplates',
        updateEmployeeFormSubmissionState:
          'employeeForm/updateEmployeeFormSubmissionState',
        exportEmployeeFormSubmissionPDF:
          'employeeForm/exportEmployeeFormSubmissionPDF'
      }),

      isOwner(submission: IEmployeeFormSubmission): boolean {
        return this.getCurrentUserFullName == submission.owner;
      },

      canCancel(submission: IEmployeeFormSubmission): boolean {
        let cancellable = false;

        if (submission.state == EmployeeFormSubmissionStateEnum.Draft) {
          cancellable = this.isOwner(submission);
        }

        if (
          submission.state == EmployeeFormSubmissionStateEnum.InReview ||
          submission.state == EmployeeFormSubmissionStateEnum.InProgress
        ) {
          cancellable = this.userHasRoles('IT Admin');
        }

        if (this.authProvider.isUserAdmin) {
          cancellable = true;
        }

        if (submission.state == EmployeeFormSubmissionStateEnum.Cancelled) {
          cancellable = false;
        }

        return cancellable;
      },

      canEdit(submission: IEmployeeFormSubmission): boolean {
        let edit = false;

        if (submission.state == EmployeeFormSubmissionStateEnum.Draft) {
          edit = this.isOwner(submission);
        }

        if (submission.state == EmployeeFormSubmissionStateEnum.InReview) {
          edit = this.isOwner(submission) || this.userHasRoles('IT Admin');
        }

        return edit;
      },

      goToDetailPage(submission: IEmployeeFormSubmission) {
        this.$router.push({
          name: 'form-request-detail',
          params: {
            id: String(submission.id)
          }
        });
      },

      openActivitiesLogDialog(request: IEmployeeFormSubmission) {
        this.$refs.employeeFormSubmissionActivityDialog.open(request);
      },

      openFormDialog(templateId: number, submission?: IEmployeeFormSubmission) {
        const template = this.getFormTemplateById(templateId);
        if (template) {
          this.$refs.employeeFormSubmissionDialog.open(template, submission);
        }
      },

      refreshSearchFilter(params: IEmployeeFormSubmissionFetchParams) {
        this.clearAllErrors();

        this.loading = true;
        this.searchEmployeeFormSubmissions({
          page: 1,
          search: this.params.search,
          ...params
        }).then(() => {
          // update params objects
          this.params.state = params.state;
          this.params.page = 1;
          this.params.form = params.form;
        });
      },

      searchEmployeeFormSubmissions(
        params: IEmployeeFormSubmissionFetchParams
      ) {
        /* this will update the URL query based on param values. */
        this.$router
          .replace({
            name: 'form-submission-list',
            query: params as Record<string, any>
          })
          .catch((e: Error) => e); //catch navigation duplication error

        return this.fetchEmployeeFormSubmissions(params)
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
            if (error.response) {
              if (error.response.data.detail) {
                this.detailErrorMessage = error.response.data.detail;
              }
            }
          })
          .finally(() => {
            this.loading = false;
          });
      },

      onUpdatePage(newPage: number) {
        if (newPage != this.params.page) {
          this.searchEmployeeFormSubmissions({
            ...this.params,
            page: newPage
          }).then(() => {
            this.params.page = newPage;
          });
        }
      },

      async debounceFetchEmployeeFormSubmissions(
        params: IEmployeeFormSubmissionFetchParams
      ) {
        this.clearAllErrors();
        this.loading = true;

        return this.debounce(this.searchEmployeeFormSubmissions, 200)(params);
      },

      openCommentDialog(
        request: IEmployeeFormSubmission,
        state: EmployeeFormSubmissionStateEnum
      ) {
        this.selectedEmployeeFormSubmission = request;
        this.overtimeComment = ''; //clears comment dialog on open
        this.commentsDialog = true;
        this.actionLabel = EmployeeFormSubmissionStateEnum[state];
        this.state = state;
      },

      closeCommentsDialog() {
        this.clearAllErrors();
        this.commentsDialog = false;
      },

      updateFormState() {
        this.commentLoading = true;

        const payload = {
          id: this.selectedEmployeeFormSubmission.id,
          state: this.state,
          comment: this.overtimeComment
        } as IUpdateEmployeeFormSubmissionStatePayload;

        this.updateEmployeeFormSubmissionState(payload)
          .then(() => {
            this.closeCommentsDialog();

            const stateInfo = new EmployeeFormSubmission({
              ...this.selectedEmployeeFormSubmission,
              state: payload.state
            }).getStateInfo();

            this.createSnack({
              color: `${stateInfo.color} darken-1`,
              type: `${stateInfo.color}`,
              right: true,
              useBtn: false,
              msg: `Form Submission ${stateInfo.display_name}`,
              btnText: 'Ok'
            });
          })
          .catch(error => {
            if (error.response) {
              if (error.response.data.detail) {
                this.commentErrorMessageDetail = error.response.data.detail;
              }
            }
            this.commentErrorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.commentLoading = false;
          });
      },

      clearAllErrors(): void {
        this.errorMessage = '';
        this.detailErrorMessage = '';
        this.commentErrorMessage = '';
        this.commentErrorMessageDetail = '';
      },

      async exportSubmissionPDF(submission: IEmployeeFormSubmission) {
        this.clearError();
        this.isExportLoading = true;
        try {
          const pdfBlobData = await this.exportEmployeeFormSubmissionPDF(
            submission.id
          );
          const fileName = `form-submission-#${submission.id}-${moment(
            new Date()
          ).format('YYYY-MM-DD-HH-mm-ss')}`;
          createPdf(pdfBlobData, fileName);

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: `PDF attachment downloaded successfully`,
            btnText: 'Ok',
            timeout: 6000
          });
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }

          this.errorMessage = ErrorManager.extractApiError(error);
        } finally {
          this.isExportLoading = false;
        }
      }
    }
  });
